/**
 * useMonitorNotificationSync — Syncs backend monitor notifications
 * into the client-side useNotifications Zustand store.
 *
 * Polls /api/cashflow/monitors/notifications for unread items
 * and injects them into the existing notification bell system.
 * Uses localStorage to track which backend notifications have
 * already been synced to avoid duplicates.
 */

import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { customInstance } from '../services/api-client';
import { useNotifications } from '../stores/useNotifications';

const SYNC_STORAGE_KEY = 'monitor-notification-synced-ids';

function getSyncedIds(): Set<number> {
  try {
    const raw = localStorage.getItem(SYNC_STORAGE_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw));
  } catch {
    return new Set();
  }
}

function addSyncedId(id: number) {
  const ids = getSyncedIds();
  ids.add(id);
  const arr = Array.from(ids).slice(-200);
  localStorage.setItem(SYNC_STORAGE_KEY, JSON.stringify(arr));
}

interface BackendNotification {
  id: number;
  title: string;
  message?: string;
  severity?: string;
  action_url?: string;
  is_read: boolean;
  created_at: string;
}

const SEVERITY_MAP: Record<string, 'info' | 'success' | 'warning' | 'error'> = {
  critical: 'error',
  warning: 'warning',
  info: 'info',
};

export function useMonitorNotificationSync() {
  const addNotification = useNotifications((s) => s.addNotification);
  const initialized = useRef(false);

  const { data } = useQuery<BackendNotification[]>({
    queryKey: ['cashflow', 'monitors', 'notifications', 'sync'],
    queryFn: () =>
      customInstance({
        url: '/api/cashflow/monitors/notifications?unread_only=true&limit=10',
        method: 'GET',
      }),
    refetchInterval: 30_000,
    retry: false,
  });

  useEffect(() => {
    if (!data?.length) return;
    if (!initialized.current) {
      initialized.current = true;
    }

    const synced = getSyncedIds();
    for (const notif of data) {
      if (synced.has(notif.id)) continue;
      addNotification({
        title: notif.title,
        message: notif.message ?? '',
        type: SEVERITY_MAP[notif.severity ?? 'info'] ?? 'info',
        actionUrl: notif.action_url,
        actionLabel: 'View Alert',
      });
      addSyncedId(notif.id);
    }
  }, [data, addNotification]);
}
